import "@src/shared/utils/transcribe/mediaRecorder.init";

import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import {
  useCreateTranscriptionSession,
  useGetEncounterById,
  useGetEncounterTranscriptLastOffset,
} from "@elation/api-client/src/stitch-web";
import {
  type AiScribeStep,
  type ClientNoteTranscriptItem,
  isErrorStep,
  isFinishingStep,
  isPausedStep,
  isRecordingActiveStep,
  resetScribingSessionId,
  useAudioInputDeviceSelect,
  useClearTranscriptItems,
  useScribe,
  useScribeReady,
  useStep,
  useTeardown,
} from "@elation/transcribe";

import {
  AuthenticatedAppHeader,
  AuthenticatedHeaderStepperV2,
} from "@src/features/auth/v2";
import { useIsInElationEHR } from "@src/features/iframe";
import { useAppBusEvent } from "@src/features/sse";
import { Button } from "@src/shared/components/ui/button";
import { navigationV2 } from "@src/shared/navigation/v2";
import { useActor } from "@src/shared/providers/actorProvider";
import { STRING_ID_NOT_READY } from "@src/shared/utils/constants";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";
import { datadogLogs } from "@src/shared/utils/observability/datadog";
import { parsePositiveIntParam } from "@src/shared/utils/params";

import { useEncounter, useEncounterIdFromParams } from "../../../application";
import { EncounterEntity } from "../../../domain";
import { VisitPageV2 } from "../../../ui/pages/v2";

const VISIT_STEPS = [
  { id: "agenda", label: "Agenda" },
  { id: "visit", label: "Visit" },
  { id: "note", label: "Note" },
];

const DEFAULT_WEBSOCKET_URL = "ws://localhost:8080/ws/transcribe";
const INITIAL_SCRIBE_STEP = "initial" satisfies AiScribeStep;

function reportVisitActionError(
  errorId: string,
  error: unknown,
  fallbackMessage: string,
  context?: Record<string, unknown>,
) {
  datadogLogs.logger.error(
    errorId,
    { errorId, ...context },
    error instanceof Error ? error : undefined,
  );
  toast.error(getServerErrorMessage(error) ?? fallbackMessage);
}

export function VisitV2Route() {
  const encounterId = useEncounterIdFromParams();
  const actor = useActor();
  const isInElationEHR = useIsInElationEHR();
  const isCreateMode = encounterId == null;
  const [isPatientSelectorOpen, setIsPatientSelectorOpen] =
    useState(isCreateMode);
  const [activeEncounterId, setActiveEncounterId] = useState<
    number | undefined
  >(undefined);

  const encounter = useGetEncounterById(encounterId ?? 0, {
    query: {
      enabled: encounterId != null,
    },
  });
  const encounterData = encounter.data?.data;
  const patientId = encounterData?.patientId;
  const {
    mutateAsync: createTranscriptionSession,
    data: createTranscriptionSessionData,
    reset: resetCreateTranscriptionSession,
  } = useCreateTranscriptionSession();
  const currentTranscriptionSession =
    createTranscriptionSessionData &&
    createTranscriptionSessionData.data.encounter.id === encounterId
      ? createTranscriptionSessionData.data
      : null;
  const scribingSessionId =
    currentTranscriptionSession?.scribingSession.id ?? null;
  const el8TranscribeSessionId =
    currentTranscriptionSession?.scribingSession.el8TranscribeSessionId ?? null;
  const lastOffset = useGetEncounterTranscriptLastOffset(
    encounterId ?? 0,
    scribingSessionId ?? STRING_ID_NOT_READY,
    {
      query: {
        enabled: encounterId != null && scribingSessionId != null,
        refetchOnMount: "always",
        staleTime: Infinity,
      },
    },
  );
  const [step, setStep] = useStep();
  const isScribeReady = useScribeReady();
  const teardownScribe = useTeardown();
  const clearTranscriptItems = useClearTranscriptItems();
  const { selectedDeviceId: microphoneDeviceId } = useAudioInputDeviceSelect();
  const isScribeStateCurrent = activeEncounterId === encounterId;
  const isScribeInProgress =
    isRecordingActiveStep(step) ||
    isPausedStep(step) ||
    isFinishingStep(step) ||
    isErrorStep(step);
  const recordingStep =
    isScribeStateCurrent || isScribeInProgress ? step : INITIAL_SCRIBE_STEP;
  const { isEncounterFinalizing } = useEncounter();

  const getScribingSessionId = useCallback(async () => {
    if (isInElationEHR) {
      throw new Error("Scribing session is not supported in iframe mode");
    }

    if (el8TranscribeSessionId) {
      return el8TranscribeSessionId;
    }

    throw new Error("Scribing session not found");
  }, [el8TranscribeSessionId, isInElationEHR]);

  const saveTranscriptItem = useCallback(
    async (_input: {
      scribingSessionId: string;
      data: ClientNoteTranscriptItem;
    }): Promise<void> => {
      return;
    },
    [],
  );

  const scribeOptions = useMemo(
    () => ({
      websocketUrl:
        actor.config.el8TranscribeWebsocketUrl ?? DEFAULT_WEBSOCKET_URL,
      getScribingSessionId,
      saveTranscriptItem,
      lastOffset: lastOffset.data?.data?.offset ?? 0,
    }),
    [
      actor.config.el8TranscribeWebsocketUrl,
      getScribingSessionId,
      lastOffset.data?.data?.offset,
      saveTranscriptItem,
    ],
  );

  const {
    audioContext,
    audioSource,
    startRecording,
    pauseRecording,
    stopRecording,
  } = useScribe(scribeOptions);

  const startVisitRecording = useCallback(async () => {
    if (isInElationEHR) {
      toast.error("Transcription is not supported in iframe mode.");
      return;
    }

    if (patientId == null) {
      toast.error("Select a patient before starting the visit.");
      return;
    }

    if (el8TranscribeSessionId) {
      if (isErrorStep(recordingStep)) {
        teardownScribe();
        clearTranscriptItems();
        setStep(INITIAL_SCRIBE_STEP);
      }

      startRecording(el8TranscribeSessionId);
      return;
    }

    if (isErrorStep(recordingStep)) {
      teardownScribe();
      clearTranscriptItems();
      setStep(INITIAL_SCRIBE_STEP);
    }

    try {
      const result = await createTranscriptionSession({
        data: {
          patientId,
        },
      });

      startRecording(result.data.scribingSession.el8TranscribeSessionId);
    } catch (error) {
      reportVisitActionError(
        "visit_v2.start_recording_failed",
        error,
        "Couldn't start the visit. Please try again.",
        { encounterId, patientId },
      );
    }
  }, [
    createTranscriptionSession,
    el8TranscribeSessionId,
    encounterId,
    patientId,
    isInElationEHR,
    clearTranscriptItems,
    recordingStep,
    setStep,
    startRecording,
    teardownScribe,
  ]);

  const resumeVisitRecording = useCallback(async () => {
    try {
      const sessionId = await getScribingSessionId();
      startRecording(sessionId);
    } catch (error) {
      reportVisitActionError(
        "visit_v2.resume_recording_failed",
        error,
        "Couldn't resume the visit. Please try again.",
        { encounterId },
      );
    }
  }, [encounterId, getScribingSessionId, startRecording]);

  const generateNote = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const handlePatientSelectorOpenChange = useCallback((open: boolean) => {
    setIsPatientSelectorOpen(open);
  }, []);

  useEffect(() => {
    if (isCreateMode) setIsPatientSelectorOpen(true);
  }, [isCreateMode]);

  const resetScribeForEncounter = useCallback(
    (nextEncounterId: number | undefined) => {
      setActiveEncounterId(nextEncounterId);
      resetCreateTranscriptionSession();
      if (isScribeInProgress) return;

      resetScribingSessionId();
      clearTranscriptItems();
      setStep(INITIAL_SCRIBE_STEP);
    },
    [
      clearTranscriptItems,
      isScribeInProgress,
      resetCreateTranscriptionSession,
      setStep,
    ],
  );

  useAppBusEvent("client.router_params_changed", ({ params }) => {
    const nextEncounterId = parsePositiveIntParam(params.encounterId);

    if (nextEncounterId !== encounterId) return;
    if (nextEncounterId === activeEncounterId) return;

    resetScribeForEncounter(nextEncounterId);
  });

  useAppBusEvent("client.router_search_params_changed", ({ searchParams }) => {
    const parsedEncounterId = parsePositiveIntParam(
      searchParams.get("encounterId"),
    );

    if (parsedEncounterId !== encounterId) return;
    if (parsedEncounterId === activeEncounterId) return;

    resetScribeForEncounter(parsedEncounterId);
  });

  return (
    <>
      <AuthenticatedAppHeader>
        <Button
          type="button"
          asChild
          variant="ghost"
          className="h-8 px-2 text-[12.5px] font-medium"
        >
          <Link to={navigationV2.visits()}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back
          </Link>
        </Button>
        <div className="flex flex-1 justify-center">
          <AuthenticatedHeaderStepperV2
            steps={VISIT_STEPS}
            currentStepId={EncounterEntity.stage({
              encounter: encounterData,
              recordingStep,
              isEncounterFinalizing,
            })}
          />
        </div>
        <div className="w-[58px]" aria-hidden="true" />
      </AuthenticatedAppHeader>
      <VisitPageV2
        encounterId={encounterId}
        patientId={patientId}
        isPatientSelectorOpen={isPatientSelectorOpen}
        isLoadingEncounter={!isCreateMode && encounter.isLoading}
        isEncounterError={!isCreateMode && encounter.isError}
        isGeneratingNote={isEncounterFinalizing}
        encounterError={encounter.error}
        recording={{
          step: recordingStep,
          isReady: isScribeReady && Boolean(microphoneDeviceId),
          isSupported: !isInElationEHR,
          microphoneDeviceId,
          audioContext,
          audioSource,
          needsMicrophoneSelection:
            !isInElationEHR &&
            recordingStep === INITIAL_SCRIBE_STEP &&
            !microphoneDeviceId,
          errorMessage: isErrorStep(recordingStep)
            ? "Check microphone permissions, then try again."
            : null,
          onStart: () => void startVisitRecording(),
          onPause: pauseRecording,
          onResume: () => void resumeVisitRecording(),
          onStop: generateNote,
        }}
        scribingSessionId={scribingSessionId}
        onPatientSelectorOpenChange={handlePatientSelectorOpenChange}
        onStartVisit={() => void startVisitRecording()}
      />
    </>
  );
}
